import { useCallback, useEffect, useRef, useState } from 'react';

import i18n from '~/locale';
import type { ConversationReference, ConversationSection } from '~/types/api.graphql';

import { MENTION_CHIP_CLASS } from './mentionChipUtils';
import {
  clearEditorIfEmpty,
  computeHasContent,
  dismissPendingMention,
  dispatchInputEvent,
  EDITOR_BLOCK_TAGS,
  getNodeText,
  getPendingChip,
  getPendingChipFromSelection,
  handleDeletionAroundInternalMarkers,
  handleModifierArrowAroundChipBoundary,
  handlePlainArrowAroundChipBoundary,
  includeChipAtSelectionFocus,
  insertClipboardText,
  insertInlineMentionReference,
  insertReferenceAtCaret as insertReferenceAtCaretDom,
  isPlainDeletionKeyEvent,
  isPlainKeyEvent,
  removeChipFromMouseEvent,
  replacePendingChipWithText,
  scanMentionQuery,
  serializeEditorContent,
  setCaretAtEndOfElement,
  shouldRemovePendingChipFromDelete,
  shouldRestoreCaretAfterPendingChipRemoval,
  wrapPendingChip,
  writeSelectionToClipboard,
} from './mentionInputDom';

const MENTION_CHIP_SELECTED_CLASS = 'mention-chip--selected';
const EDITOR_EMPTY_HTML = '<br>';

const MAX_LINES = 5;
const TYPEWRITER_INTERVAL_MS = 28;

const getPromptKey = (conversationSection: ConversationSection) =>
  ['aiInsights.prompts', conversationSection].join('.');

interface UseAIMentionInputOptions {
  section: ConversationSection | null;
  animateTyping?: boolean;
  isOpen: boolean;
  clickCounterCTA: number;
}

interface HandleMentionKeyDownOptions {
  onSubmit: () => void;
}

const isDeletionKey = (e: React.KeyboardEvent<HTMLDivElement>) => e.key === 'Backspace' || e.key === 'Delete';

const hasDeletionModifier = (e: React.KeyboardEvent<HTMLDivElement>) => e.metaKey || e.altKey || e.ctrlKey;

// Native range/modifier deletion can leave pending-chip spans behind; normalize them first.
const handlePendingChipDeletion = ({
  e,
  editor,
  clearTypingTimer,
  clearMentionState,
}: {
  e: React.KeyboardEvent<HTMLDivElement>;
  editor: HTMLElement;
  clearTypingTimer: () => void;
  clearMentionState: () => void;
}) => {
  if (!isDeletionKey(e)) return false;

  const pendingChip = getPendingChip(editor);
  if (!pendingChip) return false;

  const sel = window.getSelection();
  if (sel && !sel.isCollapsed && sel.rangeCount > 0) {
    e.preventDefault();
    sel.getRangeAt(0).deleteContents();
    clearTypingTimer();
    clearMentionState();
    dispatchInputEvent(editor);
    return true;
  }

  if (!hasDeletionModifier(e)) return false;

  replacePendingChipWithText({
    pendingChip,
    text: getNodeText(pendingChip),
    restoreCaret: shouldRestoreCaretAfterPendingChipRemoval(editor, pendingChip),
  });
  clearTypingTimer();
  clearMentionState();
  return true;
};

export const useAIMentionInput = ({
  section,
  animateTyping = false,
  isOpen,
  clickCounterCTA,
}: UseAIMentionInputOptions) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const savedHTMLRef = useRef('');
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingRunRef = useRef(0);
  const appendNodeRef = useRef<Text | null>(null);
  const savedCaretRangeRef = useRef<Range | null>(null);

  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [hasContent, setHasContent] = useState(false);

  const editorCallbackRef = useCallback((el: HTMLDivElement | null) => {
    // Preserve content across sidebar unmount/remount cycles.
    if (el === null) {
      savedHTMLRef.current = editorRef.current?.innerHTML ?? '';
    } else {
      if (savedHTMLRef.current && savedHTMLRef.current !== EDITOR_EMPTY_HTML) {
        el.innerHTML = savedHTMLRef.current;
        setHasContent(computeHasContent(el));
      }
      savedHTMLRef.current = '';
    }
    editorRef.current = el;
  }, []);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return undefined;

    const clampHeight = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
      const maxHeight = lineHeight * MAX_LINES;
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
      el.style.maxHeight = `${maxHeight}px`;
    };

    const observer = new ResizeObserver(clampHeight);
    observer.observe(el);
    clampHeight();

    return () => observer.disconnect();
  }, []);

  // Highlight/unhighlight finalized chips that fall inside the browser selection.
  // selectionchange fires outside React's event system; must use native listener.
  useEffect(() => {
    const updateChipSelection = () => {
      const editor = editorRef.current;
      if (!editor) return;

      const chips = editor.querySelectorAll<HTMLElement>(`.${MENTION_CHIP_CLASS as string}`);
      if (chips.length === 0) return;

      const sel = window.getSelection();
      const hasRange = sel && !sel.isCollapsed && sel.rangeCount > 0 && editor.contains(sel.focusNode);
      const range = hasRange ? sel.getRangeAt(0) : null;

      chips.forEach((chip) => {
        const isInRange = range !== null && range.intersectsNode(chip);
        chip.classList.toggle(MENTION_CHIP_SELECTED_CLASS, isInRange);
      });
    };

    document.addEventListener('selectionchange', updateChipSelection);
    return () => document.removeEventListener('selectionchange', updateChipSelection);
  }, []);

  const clearTypingTimer = useCallback(() => {
    // Generation token: stale typeChar callbacks self-abort after a newer run starts.
    typingRunRef.current += 1;
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (animateTyping) return;
    // clickCounterCTA retriggers prefill on repeated CTA clicks.
    const key = section ? getPromptKey(section) : '';
    if (section && isOpen && editorRef.current && i18n.exists(key)) {
      const text = String(i18n.t(key));
      const current = getNodeText(editorRef.current);
      if (!current.includes(text)) {
        const combined = current ? `${current}\n${text}` : text;
        editorRef.current.textContent = combined;
        setHasContent(combined.trim().length > 0);
      }
    }
  }, [isOpen, section, clickCounterCTA, animateTyping]);

  useEffect(() => {
    if (!isOpen) clearTypingTimer();
  }, [isOpen, clearTypingTimer]);

  useEffect(() => () => clearTypingTimer(), [clearTypingTimer]);

  const startTypingPrefill = useCallback(() => {
    clearTypingTimer();

    if (appendNodeRef.current) {
      const stale = appendNodeRef.current;
      const parent = stale.parentNode;
      stale.textContent = '';
      if (parent && parent !== editorRef.current && parent instanceof HTMLElement && !parent.textContent?.trim()) {
        parent.remove();
      }
      appendNodeRef.current = null;
    }

    const key = section ? getPromptKey(section) : '';
    if (!section || !isOpen || !editorRef.current || !i18n.exists(key)) return;

    const text = String(i18n.t(key));
    if (!text) return;

    if (getNodeText(editorRef.current).includes(text)) return;

    // Preserve browser-created empty lines so animated text does not collapse line breaks.
    const lastChild = editorRef.current.lastChild;
    const lastChildIsBlock = lastChild instanceof HTMLElement && EDITOR_BLOCK_TAGS.includes(lastChild.tagName);
    const lastChildIsEmptyBlock = lastChildIsBlock && (lastChild.textContent ?? '').trim() === '';
    const lastChildIsBR = lastChild instanceof HTMLElement && lastChild.tagName === 'BR';

    let appendNode: Text;
    let prefix = '';
    if (lastChildIsEmptyBlock) {
      lastChild.innerHTML = '';
      appendNode = document.createTextNode('');
      lastChild.appendChild(appendNode);
    } else if (lastChildIsBR) {
      appendNode = document.createTextNode('');
      editorRef.current.replaceChild(appendNode, lastChild);
    } else if (lastChildIsBlock) {
      const block = document.createElement('div');
      appendNode = document.createTextNode('');
      block.appendChild(appendNode);
      editorRef.current.appendChild(block);
    } else {
      const existing = getNodeText(editorRef.current);
      prefix = existing && !existing.endsWith('\n') ? '\n' : '';
      appendNode = document.createTextNode('');
      editorRef.current.appendChild(appendNode);
    }

    appendNodeRef.current = appendNode;

    let index = 1;
    const typingRun = typingRunRef.current;
    const typeChar = () => {
      const editor = editorRef.current;
      if (!editor || typingRunRef.current !== typingRun || !editor.contains(appendNode)) return;

      appendNode.textContent = prefix + text.slice(0, index);
      setHasContent(computeHasContent(editor));
      // Keep caret at the end so it follows the typed characters.
      const sel = window.getSelection();
      if (sel) setCaretAtEndOfElement(sel, editor);
      index++;
      if (index <= text.length) {
        typingTimerRef.current = setTimeout(typeChar, TYPEWRITER_INTERVAL_MS);
      } else {
        typingTimerRef.current = null;
        appendNodeRef.current = null;
      }
    };

    typeChar();
  }, [section, isOpen, clearTypingTimer]);

  const clearMentionState = useCallback(() => {
    editorRef.current?.removeAttribute('data-mention-pending');
    setMentionQuery(null);
  }, []);

  const finalizeEditorMutation = useCallback(
    (editor: HTMLElement) => {
      clearTypingTimer();
      clearMentionState();
      setHasContent(computeHasContent(editor));
      dispatchInputEvent(editor);
    },
    [clearMentionState, clearTypingTimer],
  );

  const handleInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    clearTypingTimer();
    savedHTMLRef.current = editor.innerHTML;
    setHasContent(computeHasContent(editor));

    const result = scanMentionQuery();
    if (result) {
      wrapPendingChip();
      setMentionQuery(result.query);
      return;
    }

    const pendingSpan = getPendingChip(editor);
    if (pendingSpan) {
      replacePendingChipWithText({
        pendingChip: pendingSpan,
        text: getNodeText(pendingSpan),
        restoreCaret: shouldRestoreCaretAfterPendingChipRemoval(editor, pendingSpan),
      });
      clearEditorIfEmpty(editor);
    }

    clearMentionState();
  }, [clearMentionState, clearTypingTimer]);

  const insertMention = useCallback(
    (ref: ConversationReference) => {
      const editor = editorRef.current;
      if (!editor) return;

      if (insertInlineMentionReference(editor, ref)) {
        finalizeEditorMutation(editor);
      }
    },
    [finalizeEditorMutation],
  );

  const saveCaretPosition = useCallback(() => {
    const editor = editorRef.current;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && sel.focusNode && editor?.contains(sel.focusNode)) {
      savedCaretRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const insertReferenceAtCaret = useCallback(
    (ref: ConversationReference) => {
      const editor = editorRef.current;
      if (!editor) return;

      if (insertReferenceAtCaretDom(editor, ref, savedCaretRangeRef.current)) {
        savedCaretRangeRef.current = null;
        finalizeEditorMutation(editor);
      }
    },
    [finalizeEditorMutation],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, { onSubmit }: HandleMentionKeyDownOptions) => {
      const isPlainKey = isPlainKeyEvent(e);
      const editor = editorRef.current;
      const pendingChip = getPendingChipFromSelection(window.getSelection());

      if (editor && pendingChip && shouldRemovePendingChipFromDelete(e, pendingChip)) {
        e.preventDefault();
        clearTypingTimer();
        replacePendingChipWithText({
          pendingChip,
          text: '',
          restoreCaret: true,
        });
        clearEditorIfEmpty(editor);
        setHasContent(computeHasContent(editor));
        clearMentionState();
        return;
      }

      if (editor && handlePendingChipDeletion({ e, editor, clearTypingTimer, clearMentionState })) {
        return;
      }

      if (isPlainDeletionKeyEvent(e) && handleDeletionAroundInternalMarkers(e.key, window.getSelection(), editor)) {
        e.preventDefault();
        return;
      }

      if (isPlainKey && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        const arrowKey: 'ArrowLeft' | 'ArrowRight' = e.key;
        if (handlePlainArrowAroundChipBoundary(arrowKey, window.getSelection(), editor)) {
          e.preventDefault();
          return;
        }
      }

      if (editor && handleModifierArrowAroundChipBoundary(e, editor)) {
        return;
      }

      if (
        e.shiftKey &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        (e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
        editor
      ) {
        includeChipAtSelectionFocus(editor, e.key === 'ArrowLeft' ? 'backward' : 'forward');
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    },
    [clearMentionState, clearTypingTimer],
  );

  const closeMention = useCallback(() => {
    const editor = editorRef.current;
    if (editor) dismissPendingMention(editor);
    clearMentionState();
  }, [clearMentionState]);

  const serialize = useCallback(() => {
    if (!editorRef.current) return '';
    return serializeEditorContent(editorRef.current);
  }, []);

  const handleCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    writeSelectionToClipboard(e, editor);
  }, []);

  const handleCut = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    const range = writeSelectionToClipboard(e, editor);
    if (!range) return;

    range.deleteContents();
    dispatchInputEvent(editor);
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    e.preventDefault();
    if (insertClipboardText(editor, e.clipboardData.getData('text/plain'))) {
      dispatchInputEvent(editor);
    }
  }, []);

  const handleEditorMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    removeChipFromMouseEvent(e, editor);
  }, []);

  const reset = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    clearTypingTimer();
    savedHTMLRef.current = '';
    setHasContent(false);
    clearMentionState();
  }, [clearMentionState, clearTypingTimer]);

  return {
    editorRef,
    editorCallbackRef,
    mentionQuery,
    hasContent,
    handleInput,
    handleKeyDown,
    handleCopy,
    handleCut,
    handlePaste,
    handleEditorMouseDown,
    insertMention,
    insertReferenceAtCaret,
    saveCaretPosition,
    closeMention,
    serialize,
    reset,
    startTypingPrefill,
  };
};
